<template>
  <form
    class="mx-auto w-full max-w-lg space-y-6"
    @submit.prevent="canPay && emit('pay')"
  >
    <fieldset :disabled="pending" class="space-y-6 disabled:opacity-60">
      <legend class="mb-6 text-2xl font-bold">
        {{ $t("pages.guide.tips.title") }}
      </legend>
      <div>
        <span id="tip-currencies" class="mb-2 block text-sm font-bold">{{
          $t("pages.guide.tips.currency")
        }}</span>
        <div
          role="group"
          aria-labelledby="tip-currencies"
          class="grid gap-2 sm:gap-3"
          :class="currencies.length === 3 ? 'grid-cols-3' : 'grid-cols-4'"
        >
          <button
            v-for="choice in currencies"
            :key="choice.currency"
            type="button"
            :aria-label="choice.currency.toUpperCase()"
            :aria-pressed="currency === choice.currency"
            class="flex min-h-16 flex-col items-center justify-center gap-1 rounded-md border px-2 py-3 font-bold transition-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
            :class="
              currency === choice.currency
                ? 'border-gold bg-gold text-black'
                : 'border-light-300 bg-white text-black hover:border-gold'
            "
            @click="currency = choice.currency"
          >
            <span aria-hidden="true" class="text-xl leading-none">{{
              symbol(choice.currency)
            }}</span>
            <span class="text-xs">{{ choice.currency.toUpperCase() }}</span>
          </button>
        </div>
      </div>
      <div>
        <span id="tip-suggestions" class="mb-2 block text-sm font-bold">{{
          $t("pages.guide.tips.chooseAmount")
        }}</span>
        <div
          role="group"
          aria-labelledby="tip-suggestions"
          class="grid grid-cols-3 gap-3"
        >
          <button
            v-for="preset in selected?.presets"
            :key="`${currency}:${preset}`"
            type="button"
            class="min-h-12 rounded-md border px-2 py-3 font-bold transition-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
            :class="
              !usingCustomAmount &&
              tipMinorUnits(amountText, selected) === preset
                ? 'border-gold bg-gold text-black'
                : 'border-light-300 bg-white text-black hover:border-gold'
            "
            :aria-pressed="
              !usingCustomAmount &&
              tipMinorUnits(amountText, selected) === preset
            "
            @click="selectPreset(preset)"
          >
            {{ preset / tipScale(selected) }}
            {{ symbol(currency) }}
          </button>
        </div>
      </div>
      <div>
        <label for="tip-amount" class="mb-2 block text-sm font-bold">{{
          $t("pages.guide.tips.customAmount")
        }}</label>
        <div
          class="flex min-h-12 items-center rounded-md border border-light-300 bg-white px-4 focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-gold"
        >
          <input
            id="tip-amount"
            :value="customAmountText"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            autocomplete="off"
            class="min-h-12 min-w-0 flex-1 bg-transparent font-bold text-black outline-none"
            :aria-invalid="usingCustomAmount && !validAmount"
            :aria-describedby="amountError ? 'tip-amount-error' : undefined"
            @input="updateCustomAmount"
          />
          <span class="font-bold text-grey">{{ currency.toUpperCase() }}</span>
        </div>
        <p
          v-if="amountError && selected"
          id="tip-amount-error"
          role="alert"
          class="mt-2 text-sm text-red"
        >
          {{
            $t("pages.guide.tips.invalidAmount", {
              min: Math.ceil(selected.minimum / tipScale(selected)),
              max: Math.floor(selected.maximum / tipScale(selected)),
              currency: currency.toUpperCase(),
            })
          }}
        </p>
      </div>
    </fieldset>
    <button
      type="submit"
      :disabled="!canPay"
      class="min-h-12 w-full rounded-md bg-gold px-6 py-4 font-bold text-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold disabled:cursor-not-allowed disabled:opacity-50"
    >
      {{ $t(pending ? "pages.guide.tips.opening" : "pages.guide.tips.pay") }}
    </button>
    <p v-if="error" role="alert" class="text-sm text-red">{{ $t(error) }}</p>
    <button
      v-if="statusUnavailable"
      type="button"
      :disabled="refreshing || pending"
      class="min-h-12 rounded-md border px-4 py-2 font-bold disabled:opacity-50"
      @click="emit('refresh')"
    >
      {{
        $t(refreshing ? "pages.guide.tips.checking" : "pages.guide.tips.retry")
      }}
    </button>
  </form>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { tipMinorUnits, tipScale, type TipCurrency } from "~/utils/guideTips";
const currency = defineModel<string>("currency", { required: true });
const amountText = defineModel<string>("amountText", { required: true });
const currencySymbols: Record<string, string> = {
  pln: "zł",
  eur: "€",
  usd: "$",
  gbp: "£",
  chf: "Fr",
  czk: "Kč",
  dkk: "kr",
  nok: "kr",
  sek: "kr",
  huf: "Ft",
  ron: "lei",
  try: "₺",
  ils: "₪",
};
const props = defineProps<{
  currencies: TipCurrency[];
  pending: boolean;
  canPay: boolean;
  validAmount: boolean;
  error: string;
  statusUnavailable?: boolean;
  refreshing?: boolean;
}>();
function symbol(code: string) {
  return (
    currencySymbols[code] ??
    new Intl.NumberFormat("en", {
      style: "currency",
      currency: code.toUpperCase(),
      currencyDisplay: "narrowSymbol",
    })
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value ??
    code.toUpperCase()
  );
}
const selected = computed(() =>
  props.currencies.find((row) => row.currency === currency.value),
);
// Presets are the payment amount, not text entered by the tourist.
const usingCustomAmount = ref(
  !selected.value?.presets.includes(
    tipMinorUnits(amountText.value, selected.value) ?? -1,
  ),
);
const amountError = computed(
  () =>
    usingCustomAmount.value && amountText.value !== "" && !props.validAmount,
);
const customAmountText = computed({
  get: () => (usingCustomAmount.value ? amountText.value : ""),
  set: (value: string) => {
    usingCustomAmount.value = true;
    amountText.value = value;
  },
});
watch(
  () => selected.value?.presets,
  (presets) => {
    // Keep a retained amount visible if an admin removes its suggested tile.
    if (
      !presets?.includes(tipMinorUnits(amountText.value, selected.value) ?? -1)
    )
      usingCustomAmount.value = true;
  },
);
function updateCustomAmount(event: Event) {
  const input = event.target as HTMLInputElement;
  if (/^\d{0,8}$/.test(input.value)) {
    customAmountText.value = input.value;
  } else {
    // Reject the entire edit/paste; never turn e.g. 12.50 into 1250.
    input.value = customAmountText.value;
  }
}
function selectPreset(preset: number) {
  usingCustomAmount.value = false;
  amountText.value = String(preset / tipScale(selected.value));
}
watch(
  currency,
  () => {
    usingCustomAmount.value = false;
  },
  { flush: "sync" },
);
const emit = defineEmits<{ pay: []; refresh: [] }>();
</script>
